import { effect } from "@preact/signals";
import * as preact from "preact";

import * as analytics from "analytics";
import * as dailyReminders from "dailyReminders";
import type { AppStores } from "stores/stores";
import * as streakRepair from "streakRepair";

interface EmailReminderAndStreakRepairProps {
  stores: AppStores;
  autoShow?: boolean;
  /**
   * Skip the streak-repair branches and only ever render the reminder opt-in
   * switch. Used by the reminderReprompt2 streak popover (StreakView) so
   * every treated user sees the same ask.
   */
  reminderOptInOnly?: boolean;
}

interface EmailReminderAndStreakRepairState {
  repairInProgress: boolean;
}

export class EmailReminderAndStreakRepair extends preact.Component<
  EmailReminderAndStreakRepairProps,
  EmailReminderAndStreakRepairState
> {
  private disposeEffect?: () => void;
  private trackedCallout?: string;

  state: EmailReminderAndStreakRepairState = {
    repairInProgress: false,
  };

  componentDidMount() {
    this.disposeEffect = effect(() => {
      void this.props.stores.user.state.value;
      void this.props.stores.streak.data.value;
      this.forceUpdate();
    });
  }

  componentWillUnmount() {
    this.disposeEffect?.();
  }

  trackCallout(name: string) {
    if (this.trackedCallout === name) return;
    this.trackedCallout = name;
    analytics.trackEventWithProperties(name, {
      "Automatically Shown": this.props.autoShow ?? false,
    });
  }

  repairStreak = () => {
    this.setState({ repairInProgress: true });
    streakRepair.repair(() => this.setState({ repairInProgress: false }));
  };

  upgrade = (event: MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
    void streakRepair.showUpsell();
  };

  render() {
    const offer = this.props.reminderOptInOnly
      ? undefined
      : streakRepair.offer(this.props.stores.streak.repairableDays);

    if (offer) {
      const { repairableDays } = offer;
      if (offer.kind === "upgrade") {
        this.trackCallout("showStreakRepairUpsell");
        return (
          <div class="callout">
            {"⚠️"} You lost a {repairableDays} streak yesterday.{" "}
            <a
              class="upgradeLink"
              href="/account"
              onClick={this.upgrade}
              target="_blank"
            >
              Upgrade to Premium
            </a>{" "}
            to repair it!
          </div>
        );
      }
      this.trackCallout("showStreakRepairCallout");
      const { repairsLeft } = offer;
      return (
        <div class="callout">
          {"⚠️"} You lost a {repairableDays} streak yesterday. But you still
          have {repairsLeft} streak repair{repairsLeft === 1 ? "" : "s"} left
          this month!
          <div style={{ marginTop: 10 }}>
            <button
              class="repairStreak"
              disabled={this.state.repairInProgress}
              onClick={this.repairStreak}
            >
              Use Streak Repair
            </button>
          </div>
        </div>
      );
    }

    return (
      <dailyReminders.DailyReminderSwitch
        label={
          dailyReminders.byNotification()
            ? "Remind me each day to help me keep my streak"
            : "Email me a daily reminder to help me keep my streak"
        }
      />
    );
  }
}
